/**
 * Настройки внешнего вида: тема, фокус-режим и звук ошибки.
 *
 * Все три живут в пространстве и потому сохраняются тем же путём, что и
 * прогресс: настройка, потерянная при перезагрузке, — это настройка, которую
 * выставляют заново каждый раз (ТЗ UX-01, UX-03).
 *
 * Вынесены из корневого хука переходов: тот и без них перерос порог, за
 * которым файл перестают читать целиком (ТЗ NFR-Q-02).
 */

import { useCallback } from 'react';
import type { ThemePreference } from '@type2x/shared-contracts';
import { updateAppearance } from '@type2x/feature-personal-workspace';

import type { Runtime } from '../runtime.js';
import type { AppFlow, WorkspaceAccess } from './useAppFlow.js';

export function useAppearanceActions(
  runtime: Runtime,
  access: WorkspaceAccess,
): Pick<
  AppFlow,
  'onThemeChange' | 'onFocusModeChange' | 'onSoundOnErrorChange' | 'onGameElementsChange'
> {
  const { workspace, commit } = access;

  const onThemeChange = useCallback(
    (theme: ThemePreference) => {
      if (workspace === null) return;
      void commit(updateAppearance(workspace, { theme }, runtime.clock.isoNow()));
    },
    [runtime, workspace, commit],
  );

  const onFocusModeChange = useCallback(
    (focusMode: boolean) => {
      if (workspace === null) return;
      void commit(updateAppearance(workspace, { focusMode }, runtime.clock.isoNow()));
    },
    [runtime, workspace, commit],
  );

  const onSoundOnErrorChange = useCallback(
    (soundOnError: boolean) => {
      if (workspace === null) return;
      void commit(updateAppearance(workspace, { soundOnError }, runtime.clock.isoNow()));
    },
    [runtime, workspace, commit],
  );

  const onGameElementsChange = useCallback(
    (gameElements: boolean) => {
      if (workspace === null) return;
      void commit(updateAppearance(workspace, { gameElements }, runtime.clock.isoNow()));
    },
    [runtime, workspace, commit],
  );

  return { onThemeChange, onFocusModeChange, onSoundOnErrorChange, onGameElementsChange };
}
